import { Breadcrumb, HeaderAction, PageHeaderLayout, useModal } from "@homebound/beam";
import { useMemo } from "react";
import { useMatch } from "react-router";
import { createConstraintItemCatalogUrl, createTaskCatalogFormUrl } from "src/RouteUrls";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, GlobalPlanTaskFilter } from "src/generated/graphqlTypes";
import { catalogTaskFolderRoute, catalogTasksRoute } from "src/routing/domains/libraries/task-catalog/taskCatalogPaths";
import { queryResult } from "src/utils";
import { TaskCatalogPageMetadataQuery, useTaskCatalogPageMetadataQuery } from "./TaskCatalogPage.generated";
import { TaskCatalogTable } from "./TaskCatalogTable";
import { MermaidLinkModal } from "./components/MermaidLinkModal";

export function TaskCatalogPage() {
  const query = useTaskCatalogPageMetadataQuery();
  return queryResult(query, { data: (data) => <TaskCatalogPageView data={data} /> });
}

type TaskCatalogPageViewProps = { data: TaskCatalogPageMetadataQuery };

function TaskCatalogPageView(props: TaskCatalogPageViewProps) {
  const { data } = props;
  const { regions, globalPlanTags, enumDetails } = data;
  const isReadOnly = !useFeatureFlag(FeatureFlagType.TaskCatalogAdmin);
  const businessFunctionTypes = enumDetails.businessFunctionType;
  const match = useMatch({ path: catalogTaskFolderRoute.path, end: false });
  const isFolderPage = match !== null;
  const { breadcrumbs, folderFilter, pageTitle } = useMemo((): {
    pageTitle: string;
    breadcrumbs: Breadcrumb[];
    folderFilter: Partial<GlobalPlanTaskFilter>;
  } => {
    const breadcrumbs: Breadcrumb[] = [{ href: catalogTasksRoute.url(), label: "Task Catalog" }];
    if (!isFolderPage) return { pageTitle: "Task Catalog", breadcrumbs, folderFilter: {} };
    const { folderId } = match.params;
    if (!folderId) return { pageTitle: "Task Catalog", breadcrumbs, folderFilter: {} };
    if (folderId.includes("globalPlanTag")) {
      const tag = globalPlanTags.find((tag) => tag.id === folderId);
      const pageTitle = `${tag?.name ?? "Tag"} Tasks`;
      return {
        pageTitle,
        breadcrumbs: [...breadcrumbs, { href: match.pathname, label: pageTitle }],
        folderFilter: { tags: [folderId] },
      };
    }
    const team = businessFunctionTypes.find((team) => team.code === folderId);
    const pageTitle = `${team?.name ?? "Team"} Tasks`;
    return {
      pageTitle,
      breadcrumbs: [...breadcrumbs, { href: match.pathname, label: pageTitle }],
      folderFilter: { team: team ? [team.code] : [] },
    };
  }, [match, isFolderPage, globalPlanTags, businessFunctionTypes]);

  const { openModal } = useModal();
  const headerActions: HeaderAction[] = useMemo(
    () => [
      ...(!isReadOnly ? [{ label: "Create", onClick: createTaskCatalogFormUrl() }] : []),
      {
        kind: "menu",
        trigger: { icon: "verticalDots", variant: "outline" },
        tooltip: "Actions",
        items: [
          { label: isReadOnly ? "View Constraints" : "Manage Constraints", onClick: createConstraintItemCatalogUrl() },
          { label: "Visualize Task Dependencies", onClick: () => openModal({ content: <MermaidLinkModal /> }) },
        ],
      },
    ],
    [isReadOnly, openModal],
  );

  return (
    <PageHeaderLayout pageHeader={{ title: pageTitle, breadcrumbs: { breadcrumbs }, actions: headerActions }}>
      <TaskCatalogTable
        businessFunctionTypes={businessFunctionTypes}
        folderFilter={folderFilter}
        globalPlanTags={globalPlanTags}
        isFolderPage={isFolderPage}
        isReadOnly={isReadOnly}
        regions={regions}
      />
    </PageHeaderLayout>
  );
}
